<template>
  <div class="signup-form">
    <img class="banner" src="@/images/signup/banner1.svg?url" />
    <div class="main">
      <div class="title">
        <div class="flex items-center gap-1">
          <img class="h-6" src="@/images/signup/tms.svg?url" />
          <div>Truck Fleet Management System</div>
        </div>
      </div>
      <div class="form">
        <el-form
          v-show="curStep == 'basic'"
          label-position="top"
          size="small"
          class="dense"
        >
          <div class="fields">
            <el-row :gutter="5">
              <el-col :sm="24" :md="12">
                <el-form-item
                  label="USDOT Number"
                  :error="errors.us_dot_number"
                  required
                >
                  <el-input
                    v-model="usDotNumber"
                    @change="validateWithDOT"
                    clearable
                  />
                </el-form-item>
              </el-col>
              <el-col :sm="24" :md="12" class="hidden">
                <el-form-item label="Company Name" required>
                  <el-input v-model="company.name" disabled />
                </el-form-item>
              </el-col>
              <el-col :sm="24" :md="12" class="hidden">
                <el-form-item label="Address" required>
                  <el-input v-model="company.address" disabled />
                </el-form-item>
              </el-col>
              <el-col :sm="10" :md="5" class="hidden">
                <el-form-item label="City" required>
                  <el-input v-model="company.city" disabled />
                </el-form-item>
              </el-col>
              <el-col :sm="8" :md="4" class="hidden">
                <el-form-item label="State" required>
                  <el-input v-model="company.state" disabled />
                </el-form-item>
              </el-col>
              <el-col :sm="6" :md="3" class="hidden">
                <el-form-item label="ZIP" required>
                  <el-input v-model="company.zip" disabled />
                </el-form-item>
              </el-col>
              <el-col :sm="24" :md="12">
                <el-form-item
                  label="Desired Subdomain"
                  :error="errors.subdomain"
                  required
                >
                  <el-input
                    v-model="subdomain"
                    placeholder="SCAC is recommended if you have one"
                    clearable
                    @change="validateSubdomain"
                  />
                </el-form-item>
              </el-col>
              <el-col :sm="24" :md="12">
                <el-form-item label="Time Zone" required>
                  <el-select
                    v-model="timeZone"
                    clearable
                    filterable
                    style="width: 100%"
                  >
                    <el-option
                      v-for="name in timeZones"
                      :key="name"
                      :label="name"
                      :value="name"
                    >
                    </el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :sm="24" :md="12">
                <el-form-item
                  label="Name of First Admin User"
                  :error="errors.admin_name"
                  required
                >
                  <el-input v-model="adminName" clearable />
                </el-form-item>
              </el-col>
              <el-col :sm="24" :md="12">
                <el-form-item
                  label="Email Company Address of First Admin User"
                  required
                >
                  <el-input
                    v-model="adminEmail"
                    type="email"
                    clearable
                    @change="validateEmail"
                  />
                </el-form-item>
              </el-col>
              <el-col :sm="12">
                <el-form-item label="Tier" required>
                  <div v-if="withInvitation" class="free-tier">
                    Free plan included with your broker invitation
                  </div>
                  <el-select v-else v-model="addOnId" filterable class="w-full">
                    <el-option
                      v-for="tier in signupPlans"
                      :key="tier.id"
                      :label="`${tier.name} ($${tier.amount} / active ${
                        tier.id == 'directory' ? 'hub' : 'trucker'
                      } / month)`"
                      :value="tier.id"
                    >
                    </el-option>
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>
            <Plans
              v-if="!freeTier"
              target="trucker"
              :tier="addOnId"
              @update:tier="addOnId = $event"
            />
            <div class="company-details">
              <slot v-if="company.name">
                <div class="flex items-center gap-1">
                  <img class="h-5" src="@/images/signup/tms.svg?url" />
                  <b>{{ company.name }}</b>
                </div>
                <p class="text-gray-600 ml-6">
                  {{ company.address }}, {{ company.city }}, {{ company.state }}
                  {{ company.zip }}
                </p>
              </slot>
              <slot v-if="company.error">
                <center class="red bold">{{ company.error }}</center>
              </slot>
            </div>
            <div v-if="freeTier">
              <div class="free-info">
                <p class="text-sm text-gray-800 m-0 leading-relaxed">
                  <span class="font-bold text-sm">{{
                    invitation.tenant.name
                  }}</span>
                  invited you to join TruckerZoom as a carrier partner and use
                  our truck fleet management system (TMS) on their network.
                </p>
                <p class="text-sm text-gray-700 m-0 mt-2 leading-relaxed">
                  Your TMS subscription for this signup is
                  <span class="font-semibold text-gray-900">free</span> - no
                  monthly software fee and no payment method required to finish
                  registration.
                </p>
              </div>
            </div>
            <div v-else>
              <div class="blue-info" v-if="addOnId">
                <div class="flex items-center gap-1">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.0049 22.0029C6.48204 22.0029 2.00488 17.5258 2.00488 12.0029C2.00488 6.48008 6.48204 2.00293 12.0049 2.00293C17.5277 2.00293 22.0049 6.48008 22.0049 12.0029C22.0049 17.5258 17.5277 22.0029 12.0049 22.0029ZM8.50488 14.0029V16.0029H11.0049V18.0029H13.0049V16.0029H14.0049C15.3856 16.0029 16.5049 14.8837 16.5049 13.5029C16.5049 12.1222 15.3856 11.0029 14.0049 11.0029H10.0049C9.72874 11.0029 9.50488 10.7791 9.50488 10.5029C9.50488 10.2268 9.72874 10.0029 10.0049 10.0029H15.5049V8.00293H13.0049V6.00293H11.0049V8.00293H10.0049C8.62417 8.00293 7.50488 9.12221 7.50488 10.5029C7.50488 11.8837 8.62417 13.0029 10.0049 13.0029H14.0049C14.281 13.0029 14.5049 13.2268 14.5049 13.5029C14.5049 13.7791 14.281 14.0029 14.0049 14.0029H8.50488Z"
                      fill="#2A51CE"
                    />
                  </svg>
                  <b>Billing Information</b>
                </div>
                <p class="text-gray-600 ml-6" v-if="directoryTier">
                  Each month, starting on <b>{{ startDate }}</b
                  >, you will be charged
                  <b>${{ priceOfSelected }}</b>
                  for each active hub you had over the previous month. You may
                  cancel at any time.
                </p>
                <p class="text-gray-600 ml-6" v-if="!directoryTier">
                  Each month, starting on <b>{{ startDate }}</b
                  >, you will be charged
                  <b>${{ priceOfSelected }}</b>
                  for each active truck driver you had over the previous month.
                  You may cancel at any time.
                </p>
              </div>
              <div id="dropin-container" />
            </div>
          </div>
        </el-form>
        <div class="fields" v-show="curStep == 'otp'">
          <e-otp :input="otp" @otpInput="otpInput" />
        </div>
        <div class="registered" v-show="curStep == 'registered'">
          <div class="text-lg md:text-xl text-center font-bold">
            Thank you for signing up for TMS
          </div>
          <p class="text-base py-4">
            Our system is currently setting up your account. This process may
            take <span class="text-2xl bold red">1</span> minute. Please be
            patient. Once the setup is complete, you will be able to access the
            TMS. If you have any questions, please click
            <a :href="contactUrl" target="_blank" class="bold">here</a> to
            contact us.
          </p>
          <el-progress
            :percentage="percentage"
            status="success"
            :stroke-width="8"
            :show-text="false"
          />
          <center class="gray">Initalizing System</center>
          <div class="text-gray-400 mt-4 hover:text-gray-700">
            It's <span class="text-blue-500 text-lg bold">OK</span> to close
            this page before the process is complete. You will be notified by
            email when your system is setup. Our system will send you an email
            with the system domain and admin login credentials.
          </div>
        </div>
      </div>
      <div class="nav" v-if="curStep != 'registered'">
        <div class="flex items-center justify-between">
          <a class="no-underline" @click="$emit('startOver')">
            <i class="fas fa-arrow-left" />
            Start Over
          </a>
          <div class="flex items-center gap-1">
            <el-button
              size="mini"
              type="primary"
              plain
              @click="prevStep"
              v-if="steps.length > 1"
            >
              <i class="fas fa-angle-left" />
              <span class="mx-4">Previous</span>
            </el-button>
            <el-button
              size="mini"
              type="primary"
              @click="handleNext"
              :disabled="nextDisabled"
            >
              <span class="mx-4">Next</span>
              <i class="fas fa-angle-right" />
            </el-button>
          </div>
        </div>
      </div>
    </div>
    <div class="hidden">
      <e-recaptcha
        ref="recaptcha"
        action="subscription"
        :site-key="recaptchaSiteKey"
      />
    </div>
  </div>
</template>
<script>
import BTDropIn from "../trucker/drop_in.js";
import Plans from "@/default/pages/signups/plans.vue";
export default {
  name: "TruckerSignup",
  mixins: [BTDropIn],
  components: { Plans },
  props: {
    invitation: {
      type: Object,
      default: () => ({}),
    },
    recaptchaSiteKey: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      steps: ["basic"],
      usDotNumber: null,
      company: {
        name: null,
        address: null,
        city: null,
        state: null,
        zip: null,
      },
      otp: {
        phone: null,
        code: null,
      },
      paymentMethod: null,
      subdomain: null,
      adminName: null,
      adminEmail: null,
      subdomainValid: false,
      emailValid: false,
      dotValid: false,
      ccValid: false,
      timeZone: "Central Time (US & Canada)",
      timeZones: [],
      processing: false,
      domain: location.hostname.replace(/^[^.]+\./, ""),
      addOns: [],
      addOnId: null,
      jobId: null,
      errors: {},
      registerTime: null,
      percentage: 0,
      contactUrl: "https://www.truckerzoom.com/contact",
    };
  },
  async mounted() {
    await this.loadOptions();
    await this.initAddOnId();
  },
  computed: {
    curStep() {
      return this.steps[this.steps.length - 1];
    },
    startDate: function () {
      const d = new Date();
      d.setMonth(d.getMonth() + 1);
      return d.toDateString();
    },
    priceOfSelected: function () {
      const y = this.addOns.find((x) => x.id == this.addOnId);
      return y?.amount;
    },
    optCode() {
      return this.otp.code || "";
    },
    withInvitation() {
      return this.invitation.tenant != null;
    },
    freeTier() {
      return this.addOnId == "free";
    },
    directoryTier() {
      return this.addOnId == "directory";
    },
    nextDisabled() {
      if (this.curStep == "basic")
        return !this.readyToSubmit || this.processing;
      if (this.curStep == "otp") return this.optCode.length != 6;
    },
    readyToSubmit() {
      return (
        this.dotValid &&
        this.subdomainValid &&
        this.emailValid &&
        this.timeZone &&
        !this.isEmptyData(this.adminName) &&
        !this.isEmptyData(this.adminEmail) &&
        (this.ccValid || this.freeTier)
      );
    },
  },
  methods: {
    prevStep() {
      this.steps.pop();
    },
    nextStep(step) {
      this.steps.push(step);
    },
    handleNext() {
      this.register();
    },
    otpInput(input) {
      this.otp = input;
    },
    async initAddOnId() {
      if (this.withInvitation) {
        this.addOnId = "free";
      } else {
        await this.initBTWeb();
      }
    },
    async loadOptions() {
      try {
        const response = await this.$http.get("/api/v1/options.json", {
          params: { target: "timezones" },
        });
        this.timeZones = response.data;
      } catch (ex) {
        console.error(ex);
      }
    },
    async validateEmail() {
      try {
        await this.$http2.get("/api/v1/subscriptions/validate_email", {
          params: {
            email: this.adminEmail,
          },
        });
        this.emailValid = true;
      } catch (ex) {
        this.adminEmail = null;
        this.emailValid = false;
      }
    },
    async validateWithDOT() {
      try {
        const { data } = await this.$http.get(
          `/api/v1/subscriptions/validate_dot?us_dot_number=${this.usDotNumber}`,
        );
        this.company.name = data.name;
        this.company.address = data.address;
        this.company.city = data.city;
        this.company.state = data.state;
        this.company.zip = data.zip;
        this.company.error = null;
        this.dotValid = true;
      } catch (ex) {
        this.usDotNumber = null;
        this.company.name = null;
        this.company.address = null;
        this.company.city = null;
        this.company.state = null;
        this.company.zip = null;
        this.company.error = ex.response.data.errors;
        this.dotValid = false;
      }
    },
    async validateSubdomain() {
      try {
        const response = await fetch(
          `https://dns.google/resolve?name=${this.subdomain}.${this.domain}`,
        );
        const { Status } = await response.json();
        switch (Status) {
          case 0:
            throw new Error(
              `Subdomain ${this.subdomain} is not available. Please try a different one.`,
            );
          case 3:
            this.subdomainValid = true;
            break;
          default:
            throw new Error(
              `Temporarily unable to verify subdomain ${this.subdomain} is not available. Please try again later.`,
            );
        }
      } catch (ex) {
        this.subdomainValid = false;
        this.$message({ message: ex, type: "error" });
      }
    },
    async register() {
      try {
        this.processing = true;
        const recaptcha_token = await this.$refs.recaptcha.getToken(
          "subscription",
        );

        let nonce, deviceData;
        if (!this.freeTier) {
          const payload = await this.dropinInstance.requestPaymentMethod();
          ({ nonce, deviceData } = payload);
        }
        const response = await this.$http.post("/api/v1/subscriptions", {
          tenant: {
            company: this.company,
            us_dot_number: this.usDotNumber,
            nonce,
            device_data: deviceData,
            subdomain: this.subdomain,
            admin_name: this.adminName,
            admin_email: this.adminEmail,
            time_zone: this.timeZone,
            addon_id: this.addOnId,
            price_per_trucker: this.priceOfSelected,
            invitation_code: this.invitation.code,
          },
          otp: this.otp,
          recaptcha_token,
        });
        if (!this.freeTier && this.dropinInstance) {
          await this.dropinInstance.teardown();
        }
        this.dotValid = false;
        this.subdomainValid = false;
        this.ccValid = false;
        this.jobId = response.data.job_id;
        this.nextStep("registered");
        await this.fetchAuth();
        this.registerTime = new Date().getTime();
        setInterval(() => {
          let diff = new Date().getTime() - this.registerTime;
          this.percentage = Math.min(100, (diff / 60000) * 100);
        }, 1000);
      } catch (ex) {
        this.processing = false;
        this.errors = ex.response.data.errors;
        if (this.errors.otp) {
          if (this.curStep != "otp") this.nextStep("otp");
          if (this.otp.code) this.$set(this.otp, "error", this.errors.otp);
        }
      }
    },
    async fetchAuth() {
      try {
        const response = await this.$http.post("/api/v1/subscriptions/auth", {
          tenant: this.subdomain,
          job_id: this.jobId,
        });
        window.location.href = response.data.url;
      } catch (ex) {
        setTimeout(async () => await this.fetchAuth(), 5000);
      }
    },
  },
};
</script>
<style lang="scss">
.signup-form {
  @apply flex items-start gap-4 my-6 px-2;

  .banner {
    @apply border hidden md:block w-72;
  }

  .main {
    @apply grow;

    .title {
      @apply text-base border-b mb-2 text-gray-700 px-2 flex flex-col gap-2;
    }

    .company-details {
      @apply w-full bg-sky-50 p-2 rounded text-xs text-gray-600 empty:hidden;
    }

    .form {
      @apply w-full m-0 p-2;

      .fields {
        @apply min-h-[300px] md:min-h-[460px];
      }

      .free-tier {
        @apply bg-green-100 border rounded text-green-600 font-bold px-2;
      }

      .free-info {
        @apply bg-sky-50 p-2 mt-3 rounded;
      }

      .blue-info {
        @apply flex flex-col gap-1 bg-sky-50 p-2 mt-3 rounded;
      }

      .braintree-placeholder {
        @apply m-0;
      }
    }

    .nav {
      @apply fixed bottom-0 left-0 w-full bg-slate-50 p-2 sm:relative;
    }

    .registered {
      @apply border p-6 bg-slate-50 rounded md:p-12;
    }

    .braintree-dropin {
      @apply border rounded p-2 mt-4;

      .braintree-heading {
        @apply text-sm leading-4;
      }

      .braintree-method {
        @apply mx-0 w-full;
      }
    }
  }
}
</style>
